// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Add project (design/agents-project-picker f-add-*): a folder already on the machine
// (browse its folders, git repositories marked, existing projects greyed), a new empty project
// (a folder with git and a first commit) or a clone of a GitHub repository with the machine's own
// `gh` (that tab only shows on machines signed in to GitHub). Konvo only sees folder names.

import styles from '@app/features/konvo_agents/components/AgentProjectPicker.module.css';
import {agentProblemMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {
	addProject,
	homeOf,
	keptFolders,
	keptRepos,
	loadFolders,
	loadRepos,
	prefetchFolders,
} from '@app/features/konvo_agents/state/AgentProjects';
import {githubOf, likelyParent, repoFolder, tildePath} from '@app/features/konvo_agents/state/AgentProjectsModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {
	AgentAddedProject,
	AgentAddProject,
	AgentFolders,
	AgentMachineView,
	AgentRepos,
} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	IconBrandGithub,
	IconChevronRight,
	IconDeviceLaptop,
	IconFolder,
	IconFolderOpen,
	IconFolderPlus,
	IconGitBranch,
	IconHome,
	IconPencil,
	IconSearch,
} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {Fragment, useEffect, useRef, useState} from 'react';

type Tab = 'folder' | 'new' | 'clone';

/** How long the pointer rests on a folder before it is listed ahead of a click. */
const PREFETCH_AFTER_MS = 120;

/** Placeholder rows while the machine lists a folder (or its GitHub repositories) the first time. */
export function LoadingRows({count = 6}: {count?: number}) {
	return (
		<div className={styles.loadingRows} aria-hidden>
			{Array.from({length: count}, (_, index) => (
				<span key={index} className={styles.loadingRow} />
			))}
		</div>
	);
}

/** Browses the machine's folders. `onTarget` says which folder the footer acts on (the picked one, else the open one). */
const FolderBrowser = observer(
	({
		machine,
		start,
		selectable,
		onTarget,
	}: {
		machine: AgentMachineView;
		start: string;
		/** Folders can be picked (Add a folder); otherwise only opened (choosing where a new one goes). */
		selectable: boolean;
		onTarget: (target: {path: string; name: string; taken: boolean} | null) => void;
	}) => {
		const {t, i18n} = useLingui();
		const [listing, setListing] = useState<AgentFolders | null>(() => keptFolders(machine.id, start));
		/** The folder the machine is listing for us ('' for home), until it answers. */
		const [opening, setOpening] = useState<string | null>(null);
		const [picked, setPicked] = useState<string | null>(null);
		const [typing, setTyping] = useState<string | null>(null);
		// Only the latest open shows: a late answer for a folder clicked before is dropped.
		const latest = useRef(0);
		const hover = useRef<ReturnType<typeof setTimeout> | null>(null);

		const show = (next: AgentFolders) => {
			setListing(next);
			setPicked(null);
			setTyping(null);
		};

		const open = async (path?: string) => {
			const at = ++latest.current;
			const kept = keptFolders(machine.id, path);
			if (kept) {
				setOpening(null);
				return show(kept);
			}
			setOpening(path ?? '');
			try {
				const next = await loadFolders(machine.id, path);
				if (at === latest.current) show(next);
			} catch (error) {
				if (at === latest.current) ToastCommands.error(agentProblemMessage(i18n, error));
			} finally {
				if (at === latest.current) setOpening(null);
			}
		};

		const restOn = (path: string) => {
			if (hover.current) clearTimeout(hover.current);
			hover.current = setTimeout(() => prefetchFolders(machine.id, path), PREFETCH_AFTER_MS);
		};
		const leave = () => {
			if (hover.current) clearTimeout(hover.current);
			hover.current = null;
		};
		useEffect(() => leave, []);

		// Opens the start folder once per machine.
		useEffect(() => {
			void open(start);
		}, [machine.id]);

		useEffect(() => {
			if (!listing) return onTarget(null);
			const chosen = picked ? listing.folders.find((f) => f.path === picked) : null;
			if (chosen) return onTarget({path: chosen.path, name: chosen.name, taken: Boolean(chosen.project)});
			const here = listing.parts[listing.parts.length - 1];
			// The open folder itself (not home or the root: those are rarely a project).
			const usable = listing.path !== listing.home && listing.parts.length > 1;
			onTarget(usable ? {path: listing.path, name: here?.name ?? listing.path, taken: false} : null);
		}, [listing, picked, onTarget]);

		const home = listing?.home ?? homeOf(machine.id);
		const homeIndex = listing ? listing.parts.findIndex((part) => part.path === listing.home) : -1;
		// From home down as ~ › nuvio › repos; outside home, from the root.
		const parts = listing ? (homeIndex >= 0 ? listing.parts.slice(homeIndex) : listing.parts) : [];

		return (
			<>
				<div
					className={styles.pathBar}
					data-busy={opening != null || undefined}
					data-flx="konvo-agents.add-project.path"
				>
					{typing != null ? (
						<input
							className={styles.pathInput}
							value={typing}
							onChange={(event) => setTyping(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									event.stopPropagation();
									void open(typing);
								} else if (event.key === 'Escape') {
									event.stopPropagation();
									setTyping(null);
								}
							}}
							aria-label={t`Folder path`}
							placeholder="~/code"
							// biome-ignore lint/a11y/noAutofocus: typing a path was just asked for.
							autoFocus
						/>
					) : (
						<div className={styles.crumbs}>
							{parts.map((part, index) => (
								<Fragment key={part.path}>
									{index > 0 && <IconChevronRight size={13} stroke={1.7} className={styles.crumbSep} />}
									<button
										type="button"
										className={clsx(styles.crumb, index === parts.length - 1 && styles.crumbLast)}
										onClick={() => void open(part.path)}
									>
										{part.path === listing?.home ? (
											<>
												<IconHome size={14} stroke={1.7} />~
											</>
										) : (
											part.name
										)}
									</button>
								</Fragment>
							))}
						</div>
					)}
					<Tooltip text={t`Type a path`} position="top">
						<button
							type="button"
							className={styles.iconButton}
							onClick={() => setTyping(typing == null ? tildePath(listing?.path, home) : null)}
							aria-label={t`Type a path`}
							data-flx="konvo-agents.add-project.type-path"
						>
							<IconPencil size={15} stroke={1.7} />
						</button>
					</Tooltip>
				</div>
				<div
					className={clsx(styles.scroll, listing && opening != null && styles.scrollBusy)}
					aria-busy={opening != null || !listing}
					data-flx="konvo-agents.add-project.folders"
				>
					{!listing && <LoadingRows />}
					{listing && listing.folders.length === 0 && <p className={styles.empty}>{t`No folders in here.`}</p>}
					{listing?.folders.map((folder) => {
						const folderName = folder.name;
						const isOpening = opening === folder.path;
						return (
							<div
								key={folder.path}
								className={clsx(
									styles.folderRow,
									picked === folder.path && styles.folderRowOn,
									folder.project && styles.folderTaken,
									isOpening && styles.folderRowOpening,
								)}
								onPointerEnter={() => restOn(folder.path)}
								onPointerLeave={leave}
								data-flx="konvo-agents.add-project.folder"
							>
								<button
									type="button"
									className={styles.folderMain}
									onClick={() =>
										selectable && !folder.project
											? setPicked(picked === folder.path ? null : folder.path)
											: void open(folder.path)
									}
									onDoubleClick={() => void open(folder.path)}
								>
									{picked === folder.path ? (
										<IconFolderOpen size={18} stroke={1.6} className={styles.folder} />
									) : (
										<IconFolder size={18} stroke={1.6} className={styles.folder} />
									)}
									<span className={styles.folderName}>{folder.name}</span>
									{folder.git && (
										<span className={styles.gitTag}>
											<IconGitBranch size={12} stroke={1.8} />
											git
										</span>
									)}
									{folder.project && <span className={styles.taken}>{t`Already a project`}</span>}
								</button>
								<Tooltip text={t`Open ${folderName}`} position="left">
									<button
										type="button"
										className={styles.open}
										onClick={() => void open(folder.path)}
										aria-label={t`Open ${folderName}`}
										aria-busy={isOpening || undefined}
									>
										{isOpening ? <Spinner size="small" /> : <IconChevronRight size={16} stroke={1.7} />}
									</button>
								</Tooltip>
							</div>
						);
					})}
					{listing?.truncated && <p className={styles.empty}>{t`Only the first 500 folders are listed.`}</p>}
				</div>
			</>
		);
	},
);

export const AgentProjectAdd = observer(
	({
		machine,
		layout,
		onCancel,
		onAdded,
	}: {
		machine: AgentMachineView;
		layout: 'panes' | 'chips';
		onCancel: () => void;
		onAdded: (added: AgentAddedProject, startChat: boolean) => void;
	}) => {
		const {t, i18n} = useLingui();
		const github = githubOf(machine);
		const githubAccount = github?.account;
		const [tab, setTab] = useState<Tab>('folder');
		const [saving, setSaving] = useState(false);
		const [startChat, setStartChat] = useState(true);
		const start = likelyParent(Agents.projectsOf(machine.id));
		const home = homeOf(machine.id);
		const machineName = machine.name;

		// Folder
		const [target, setTarget] = useState<{path: string; name: string; taken: boolean} | null>(null);
		// New and Clone: where it goes, chosen by browsing when asked.
		const [parent, setParent] = useState(start);
		const [choosingParent, setChoosingParent] = useState(false);
		const [parentTarget, setParentTarget] = useState<{path: string; name: string} | null>(null);
		const [name, setName] = useState('');
		// Clone
		const [repos, setRepos] = useState<AgentRepos | null>(() => keptRepos(machine.id));
		const [repoQuery, setRepoQuery] = useState('');
		const [repo, setRepo] = useState<string | null>(null);

		useEffect(() => {
			if (tab !== 'clone' || repos) return;
			loadRepos(machine.id)
				.then(setRepos)
				.catch((error) => ToastCommands.error(agentProblemMessage(i18n, error)));
		}, [tab, repos, machine.id, i18n]);

		const add = async (request: AgentAddProject) => {
			setSaving(true);
			try {
				const added = await addProject(machine.id, request);
				const addedTitle = added.title;
				ToastCommands.success(t`Added ${addedTitle} on ${machineName}.`);
				onAdded(added, startChat);
			} catch (error) {
				ToastCommands.error(agentProblemMessage(i18n, error));
			} finally {
				setSaving(false);
			}
		};

		const tabs: Array<{id: Tab; label: string; icon: React.ReactNode}> = [
			{
				id: 'folder',
				label: layout === 'chips' ? t`Folder` : t`Folder on ${machineName}`,
				icon: <IconFolder size={15} stroke={1.6} />,
			},
			{id: 'new', label: layout === 'chips' ? t`New` : t`New project`, icon: <IconFolderPlus size={15} stroke={1.6} />},
			...(github
				? [
						{
							id: 'clone' as const,
							label: layout === 'chips' ? t`GitHub` : t`Clone from GitHub`,
							icon: <IconBrandGithub size={15} stroke={1.6} />,
						},
					]
				: []),
		];

		const startToggle = (
			<label className={styles.toggle}>
				<input type="checkbox" checked={startChat} onChange={(event) => setStartChat(event.target.checked)} />
				{t`Start a chat in it`}
			</label>
		);

		const parentPicker = (
			<button
				type="button"
				className={styles.pick}
				onClick={() => setChoosingParent(true)}
				data-flx="konvo-agents.add-project.parent"
			>
				<IconFolder size={16} stroke={1.6} />
				<span className={styles.pickPath}>
					{tildePath(parent, home)}
					{tab === 'clone' && repo ? `/${repoFolder(repo)}` : ''}
				</span>
				<span className={styles.pickChange}>{t`Change`}</span>
			</button>
		);

		const targetName = target?.name;
		const parentTargetName = parentTarget?.name;
		let content: React.ReactNode;
		let foot: React.ReactNode;
		if (choosingParent) {
			content = <FolderBrowser machine={machine} start={parent} selectable={false} onTarget={setParentTarget} />;
			foot = (
				<>
					<span className={styles.footNote}>
						<IconFolder size={14} stroke={1.6} />
						{parentTarget ? tildePath(parentTarget.path, home) : ''}
					</span>
					<Button variant="secondary" onClick={() => setChoosingParent(false)}>
						{t`Back`}
					</Button>
					<Button
						disabled={!parentTarget}
						onClick={() => {
							if (parentTarget) setParent(parentTarget.path);
							setChoosingParent(false);
						}}
					>
						{parentTargetName ? t`Use ${parentTargetName}` : t`Use this folder`}
					</Button>
				</>
			);
		} else if (tab === 'folder') {
			content = <FolderBrowser machine={machine} start={start} selectable onTarget={setTarget} />;
			foot = (
				<>
					<span className={styles.footNote}>
						{target && (
							<>
								<IconFolder size={14} stroke={1.6} />
								{tildePath(target.path, home)}
							</>
						)}
					</span>
					{startToggle}
					<Button
						submitting={saving}
						disabled={!target || target.taken}
						onClick={() => target && void add({kind: 'folder', path: target.path})}
					>
						{targetName ? t`Add ${targetName}` : t`Add project`}
					</Button>
				</>
			);
		} else if (tab === 'new') {
			const trimmed = name.trim();
			content = (
				<div className={styles.form}>
					<label className={styles.field}>
						<span className={styles.fieldLabel}>{t`Name`}</span>
						<input
							className={styles.input}
							value={name}
							maxLength={100}
							onChange={(event) => setName(event.target.value)}
							placeholder="client-portal"
							// biome-ignore lint/a11y/noAutofocus: the tab is for typing a name.
							autoFocus
							data-flx="konvo-agents.add-project.name"
						/>
					</label>
					<div className={styles.field}>
						<span className={styles.fieldLabel}>{t`Create it in`}</span>
						{parentPicker}
					</div>
					{trimmed && (
						<div className={styles.preview}>
							<IconFolderPlus size={18} stroke={1.6} />
							<div>
								<strong>{`${tildePath(parent, home)}/${trimmed}`}</strong>
								<span>{t`New folder · git · first commit`}</span>
							</div>
						</div>
					)}
					{startToggle}
				</div>
			);
			foot = (
				<>
					<span className={styles.footNote}>
						<IconDeviceLaptop size={14} stroke={1.6} />
						{t`On ${machineName}`}
					</span>
					<Button variant="secondary" onClick={onCancel}>
						{t`Cancel`}
					</Button>
					<Button
						submitting={saving}
						disabled={!trimmed}
						onClick={() => void add({kind: 'new', parent, name: trimmed})}
					>
						{t`Create project`}
					</Button>
				</>
			);
		} else {
			const needle = repoQuery.trim().toLocaleLowerCase();
			const shown = (repos?.repos ?? []).filter((r) => !needle || r.name.toLocaleLowerCase().includes(needle));
			// Typed straight in as owner/name, when the list doesn't have it.
			const typed = /^[\w.-]+\/[\w.-]+$/.test(repoQuery.trim()) && !shown.some((r) => r.name === repoQuery.trim());
			content = (
				<>
					<div className={clsx(styles.pad, styles.padTight)}>
						<label className={styles.search}>
							<IconSearch size={17} stroke={1.7} />
							<input
								value={repoQuery}
								onChange={(event) => setRepoQuery(event.target.value)}
								placeholder={t`Search your GitHub repositories`}
								aria-label={t`Search your GitHub repositories`}
								data-flx="konvo-agents.add-project.repo-search"
							/>
						</label>
					</div>
					<div className={styles.scroll} data-flx="konvo-agents.add-project.repos">
						{!repos && <LoadingRows count={5} />}
						{typed && (
							<button
								type="button"
								className={clsx(styles.row, styles.repoRow, repo === repoQuery.trim() && styles.rowActive)}
								onClick={() => setRepo(repoQuery.trim())}
							>
								<IconBrandGithub size={18} stroke={1.6} className={styles.folder} />
								<span className={styles.repoText}>
									<strong>{repoQuery.trim()}</strong>
									<span>{t`Clone this repository`}</span>
								</span>
							</button>
						)}
						{repos && shown.length === 0 && !typed && <p className={styles.empty}>{t`No repositories match.`}</p>}
						{shown.map((r) => (
							<button
								key={r.name}
								type="button"
								className={clsx(styles.row, styles.repoRow, repo === r.name && styles.rowActive)}
								onClick={() => setRepo(repo === r.name ? null : r.name)}
								data-flx="konvo-agents.add-project.repo"
							>
								<IconBrandGithub size={18} stroke={1.6} className={styles.folder} />
								<span className={styles.repoText}>
									<strong>{r.name}</strong>
									<span>{[r.private ? t`Private` : t`Public`, r.description].filter(Boolean).join(' · ')}</span>
								</span>
							</button>
						))}
					</div>
					<div className={styles.cloneInto}>
						<span className={styles.fieldLabel}>{t`Clone into`}</span>
						{parentPicker}
					</div>
				</>
			);
			foot = (
				<>
					<span className={styles.footNote}>
						<IconBrandGithub size={14} stroke={1.6} />
						{githubAccount ? <Trans>Signed in as {githubAccount}</Trans> : <Trans>Signed in to GitHub</Trans>}
					</span>
					{startToggle}
					<Button
						submitting={saving}
						disabled={!repo}
						onClick={() => repo && void add({kind: 'clone', parent, name: repoFolder(repo), repository: repo})}
					>
						{saving ? t`Cloning…` : t`Clone and add`}
					</Button>
				</>
			);
		}

		return (
			<div className={styles.main} data-flx="konvo-agents.add-project">
				{!choosingParent && (
					<div className={styles.pad}>
						<div className={styles.tabs} role="tablist">
							{tabs.map((entry) => (
								<button
									key={entry.id}
									type="button"
									role="tab"
									aria-selected={tab === entry.id}
									className={clsx(styles.tab, tab === entry.id && styles.tabOn)}
									onClick={() => setTab(entry.id)}
									data-flx={`konvo-agents.add-project.tab-${entry.id}`}
								>
									{entry.icon}
									{entry.label}
								</button>
							))}
						</div>
					</div>
				)}
				{content}
				<footer className={styles.foot}>{foot}</footer>
			</div>
		);
	},
);
